<template>
    <v-container fluid class="form-content overflow-auto pa-5">
        <v-form ref="form" :disabled="!writable" @input="$emit('input', $event)">
            <h4 class="mb-2 mt-5">{{ $t('infos') }}</h4>
            <!-- Bezeichnung -->
            <v-row dense>
                <v-col cols="12" class="d-flex align-center" style="gap: 5px">
                    <LabelField
                        class="flex-grow-1"
                        :label="addressToEdit?.legal_type === 'person' ? $t('bezeichnung_person') : $t('bezeichnung')"
                    >
                        <v-text-field
                            :value="addressToEdit?.title"
                            dense
                            outlined
                            hide-details="auto"
                            :placeholder="$t('location_edit_title_placeholder')"
                            :counter="60"
                            :rules="
                                [
                                    addressToEdit?.legal_type === 'organization' ? rules.required : null,
                                    rules.maxLength(60),
                                ].filter(Boolean)
                            "
                            @input="addressToEdit?.set('title', $event)"
                        />
                    </LabelField>
                    <AddressIconBar :item="addressToEdit" hide-status-icon />
                </v-col>
            </v-row>

            <!-- Adresse -->
            <h4 class="mb-2 mt-5">{{ $t('adresse') }}</h4>
            <v-row dense>
                <v-col cols="12" lg="4" xl="5">
                    <LabelField :label="$t('strasse')">
                        <v-text-field
                            :value="addressToEdit?.street"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.required, rules.maxLength(100)].filter(Boolean)"
                            @input="addressToEdit?.set('street', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="2" xl="1">
                    <LabelField :label="$t('strasse_nr')">
                        <v-text-field
                            :value="addressToEdit?.street_nr"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(15)]"
                            @input="addressToEdit?.set('street_nr', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('zusatz')">
                        <v-text-field
                            :value="addressToEdit?.address_supplement"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(100)]"
                            @input="addressToEdit?.set('address_supplement', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <!-- plz/zip, ort -->
            <v-row dense>
                <v-col cols="12" lg="6">
                    <ZipCityGroup :record="addressToEdit" />
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('postfach')">
                        <v-text-field
                            :value="addressToEdit?.po_box"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(100)]"
                            @input="addressToEdit?.set('po_box', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <!-- land -->
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('land')">
                        <CountrySelectField
                            :value="addressToEdit?.country_id"
                            hide-details="auto"
                            item-value="id"
                            :label="null"
                            @input="addressToEdit?.set('country_id', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>

            <!-- Location specific -->
            <h4 class="mt-5 mb-2">{{ $t('zusatzinfos') }}</h4>
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('opening_hours')" :label-tip="$t('location_edit_opening_hours_label_tip')">
                        <v-textarea
                            :value="locationToEdit?.opening_hours"
                            dense
                            rows="8"
                            outlined
                            hide-details="auto"
                            :counter="250"
                            :rules="[rules.maxLength(250)]"
                            @input="locationToEdit?.set('opening_hours', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('accessibility')" :label-tip="$t('accessibility_label_location_tip')">
                        <v-textarea
                            :value="locationToEdit?.accessibility"
                            dense
                            outlined
                            :placeholder="$t('placeholder_accessibility_location')"
                            hide-details="auto"
                            :counter="1000"
                            :rules="[rules.maxLength(1000)]"
                            @input="locationToEdit?.set('accessibility', $event)"
                        />
                    </LabelField>
                    <LabelField
                        :label="$t('accessibility_link_location')"
                        :label-tip="$t('accessibility_link_location_tip')"
                    >
                        <AccessibilityUrlField
                            :location-id="locationToEdit?.id"
                            :accessibility-url="locationToEdit?.accessibility_link"
                            @input="locationToEdit?.set('accessibility_link', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>

            <h4 class="mt-5 mb-2">{{ $t('contact') }}</h4>
            <!-- tel -->
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('tel_1')">
                        <v-text-field
                            :value="addressToEdit?.tel"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(50)]"
                            @input="addressToEdit?.set('tel', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('tel_2')">
                        <v-text-field
                            :value="addressToEdit?.mobile"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(50)]"
                            @input="addressToEdit?.set('mobile', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <!-- mail -->
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('email')">
                        <v-text-field
                            :value="addressToEdit?.email"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.email, rules.maxLength(255)]"
                            @input="addressToEdit?.set('email', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('webseite')">
                        <UrlField
                            :value="addressToEdit?.web"
                            :rules="[rules.maxLength(255)]"
                            @input="addressToEdit?.set('web', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<script>
import { Address, AddressCollection } from '@/models/Address';
import { Location } from '@/models/Location';
import { formRules } from '@/helpers/tools';
import CountrySelectField from '@/components/country/SelectField';
import { mapActions } from 'vuex';
import AddressIconBar from '@/components/address/AddressIconBar.vue';
import AccessibilityUrlField from '@/components/form/AccessibilityUrlField.vue';
import ZipCityGroup from '../address/ZipCityGroup.vue';

export default {
    name: 'LocationEditForm',
    components: {
        CountrySelectField,
        AddressIconBar,
        AccessibilityUrlField,
        ZipCityGroup,
    },

    props: {
        location: {
            type: Location,
            default: null,
        },
        organizerId: {
            type: Number,
            default: null,
        },
        writable: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            locationToEdit: null,
            addressToEdit: null,
            loading: true,
        };
    },

    watch: {
        location: {
            handler(newValue) {
                this.locationToEdit = newValue?.clone() || new Location({ event_organizer_id: this.organizerId });
            },
            immediate: true,
        },
        'location.address_id': {
            async handler(newValue) {
                if (newValue) {
                    this.$emit('loading', true);
                    this.addressToEdit = await this.loadAddressForId(this.location?.address_id);
                    this.$emit('loading', false);
                } else {
                    this.addressToEdit = new Address({ type: 'location', legal_type: 'organization' });
                }
                this.validateForm();
            },
            immediate: true,
        },
        'addressToEdit.id': {
            handler(newValue) {
                this.locationToEdit.address_id = newValue;
            },
            immediate: true,
        },
    },

    created() {
        this.rules = formRules;
    },

    mounted() {
        this.validateForm();
    },

    methods: {
        ...mapActions(['runAsync']),

        validateForm() {
            this.$refs.form?.validate();
        },

        async loadAddressForId(id) {
            if (!id) {
                return null;
            }
            const collection = new AddressCollection();
            await this.runAsync(async () => await collection.query({ id }));
            return collection.first();
        },

        async save() {
            const { addressChanged } = await this.saveAddress();
            const { locationIsNew, locationChanged } = await this.saveLocation();

            if (locationIsNew) {
                this.$emit('location-added', {
                    location: this.locationToEdit,
                    address: this.addressToEdit,
                });
            } else if (locationChanged) {
                this.$emit('location-changed', {
                    location: this.locationToEdit,
                    address: this.addressToEdit,
                });
            } else if (addressChanged) {
                this.$emit('address-changed', {
                    location: this.locationToEdit,
                    address: this.addressToEdit,
                });
            }
        },

        async saveAddress() {
            const addressIsNew = this.addressToEdit?.isPhantom() ?? false;
            const addressSaveNeeded = addressIsNew || (this.addressToEdit?.isDirty() ?? false);

            if (addressSaveNeeded) {
                await this.runAsync(async () => await this.addressToEdit.save());
            }
            return {
                addressIsNew,
                addressChanged: addressSaveNeeded,
            };
        },

        async saveLocation() {
            const locationIsNew = this.locationToEdit?.isPhantom() ?? false;
            const locationSaveNeeded = locationIsNew || (this.locationToEdit?.isDirty() ?? false);
            if (locationSaveNeeded) {
                await this.runAsync(async () => await this.locationToEdit.save());
            }
            return {
                locationIsNew,
                locationChanged: locationSaveNeeded,
            };
        },
    },
};
</script>

<style lang="scss" scoped></style>
